import { tokenVar } from '@shared/design-system';

/**
 * PortfolioRow 의 칸 정의(Figma 2047:18083). 표는 부모 폭을 채우고(DD-15 H2) 칸은 `px` 의 비율로 나눈다.
 * 표 폭이 칸 최소 폭의 합보다 좁아지면 줄이지 않고 가로로 스크롤한다(부모가 overflow-x-auto).
 * `px` 는 Figma 1380(DD-19) 에서의 칸 폭(scope=all) — 칸은 이 비율로 표 폭을 나눈다. Figma 는 앞 6칸이 최소 폭 그대로, 뒤 5칸이 남는 폭을 똑같이(102) 나눈다.
 * `skeletonPx` 는 status=init 막대 폭 — Figma S04 로딩(`2143:22449`)의 칸별 막대 폭 그대로(Figma 막대는 칸 폭과 상관없이 고정, DD-19).
 *   표 칸 너비와 같은 표마다의 명세라 칸 정의에 둔다(raw 예외, R4). 칸이 좁으면 칸 폭에 맞춘다.
 * `minPx` 는 Figma 칸의 최소 폭(scope=all, Figma 1380 과 1320 모두 같다). 표 칸 너비는 표마다의 명세라 토큰으로 만들지 않는다(raw 예외, R4).
 */
export const PORTFOLIO_ROW_COLUMNS = [
  { key: 'portfolioName', minPx: 174, px: 174, merged: true, skeletonPx: 129 },
  { key: 'fundName', minPx: 194, px: 194, merged: false, skeletonPx: 144 },
  { key: 'investAt', minPx: 116, px: 116, merged: false, skeletonPx: 84 },
  { key: 'investVolume', minPx: 135, px: 135, merged: false, skeletonPx: 99 },
  { key: 'valuation', minPx: 135, px: 135, merged: false, skeletonPx: 99 },
  { key: 'valuatedAt', minPx: 116, px: 116, merged: false, skeletonPx: 84 },
  { key: 'irr', minPx: 97, px: 102, merged: false, skeletonPx: 69 },
  { key: 'multiple', minPx: 77, px: 102, merged: false, skeletonPx: 54 },
  { key: 'internalManagerName', minPx: 97, px: 102, merged: true, skeletonPx: 69 },
  { key: 'revenueHistory', minPx: 87, px: 102, merged: true, skeletonPx: 62 },
  { key: 'stockPriceHistory', minPx: 87, px: 102, merged: true, skeletonPx: 62 },
] as const;

export type PortfolioRowColumnKey = (typeof PORTFOLIO_ROW_COLUMNS)[number]['key'];
export type PortfolioRowScope = 'all' | 'fund';

/** scope 별 칸: fund 는 펀드명 칸이 없다 */
export const PORTFOLIO_ROW_SCOPE_COLUMNS: Record<
  PortfolioRowScope,
  readonly (typeof PORTFOLIO_ROW_COLUMNS)[number][]
> = {
  all: PORTFOLIO_ROW_COLUMNS,
  fund: PORTFOLIO_ROW_COLUMNS.filter((column) => column.key !== 'fundName'),
};

/** 표의 최소 폭 = 칸 최소 폭의 합(all 1315 · fund 1121). 이보다 좁으면 가로 스크롤 */
export const PORTFOLIO_ROW_MIN_WIDTH_PX: Record<PortfolioRowScope, number> = {
  all: PORTFOLIO_ROW_SCOPE_COLUMNS.all.reduce((sum, column) => sum + column.minPx, 0),
  fund: PORTFOLIO_ROW_SCOPE_COLUMNS.fund.reduce((sum, column) => sum + column.minPx, 0),
};

/** scope 별 스켈레톤 막대 폭(px, 칸 순서대로) */
export const PORTFOLIO_ROW_SKELETON_PX: Record<PortfolioRowScope, readonly number[]> = {
  all: PORTFOLIO_ROW_SCOPE_COLUMNS.all.map((column) => column.skeletonPx),
  fund: PORTFOLIO_ROW_SCOPE_COLUMNS.fund.map((column) => column.skeletonPx),
};

/** scope 별 Figma 칸 폭의 합(비율의 분모) */
const PORTFOLIO_ROW_PX_TOTAL: Record<PortfolioRowScope, number> = {
  all: PORTFOLIO_ROW_SCOPE_COLUMNS.all.reduce((sum, column) => sum + column.px, 0),
  fund: PORTFOLIO_ROW_SCOPE_COLUMNS.fund.reduce((sum, column) => sum + column.px, 0),
};

/** 칸 폭 = 표 폭 × (Figma 칸 폭 / 합). 머리 줄 · 본문 줄 칸 모두에 준다(table-fixed 는 첫 줄로 칸 폭을 정한다) */
export function portfolioRowColumnWidth(scope: PortfolioRowScope, px: number): string {
  return `${(px / PORTFOLIO_ROW_PX_TOTAL[scope]) * 100}%`;
}

/** 표: 부모 폭을 채우고 칸 폭은 머리 줄에서 정한다 */
export const PORTFOLIO_ROW_TABLE_CLASS = 'w-full table-fixed';

/** 값 한 줄의 높이(54 = 위아래 16 + 글자 20 + 선 2, `size.table-row`). 여러 줄이면 줄 수만큼 곱한다(두 줄 108) */
export const PORTFOLIO_ROW_LINE_HEIGHT = tokenVar['size.table-row'];

/** 머리 줄 칸: 좌우 10, 글자 label, 오른쪽 화살표, 아래 2px 선 */
export const PORTFOLIO_ROW_HEADER_CELL_CLASS =
  'border-b-2 border-bg-canvas bg-bg-surface px-2-5 pt-3 pb-2-5 text-left align-middle text-fg-default typo-label';

/** 본문 칸의 아래 선: 병합 칸이 이어지면(continued) 면과 같은 색으로 자리만 남긴다 */
export const PORTFOLIO_ROW_CELL_BORDER_CLASS = {
  line: 'border-b-2 border-bg-canvas',
  continued: 'border-b-2 border-bg-surface',
} as const;

/** 본문 칸 */
export const PORTFOLIO_ROW_CELL_CLASS =
  'bg-bg-surface p-0 text-left align-middle text-fg-default typo-body-sm';

/** 값이 한 줄인 칸의 안쪽: 좌우 8 · 위아래 16 */
export const PORTFOLIO_ROW_ITEM_CLASS = 'flex items-center gap-1 px-2 py-4';

/**
 * 값이 여러 줄인 칸(투자 · 회수가 여러 건): 줄 하나가 한 줄 자리(54)를 그대로 차지하고 위에서부터 쌓는다 —
 * 줄 높이가 줄 수 × 54 이므로 칸마다 줄이 같은 자리에 맞는다(예전엔 36 · 38 줄이 가운데로 몰려 위아래가 비었다)
 */
export const PORTFOLIO_ROW_STACK_CLASS = 'flex flex-col px-2';

/** 여러 줄 칸의 줄: 둘째 줄부터 위에 2px 틈(배경색 선) */
export const PORTFOLIO_ROW_STACK_LINE_CLASS = {
  first: 'flex items-center gap-1 overflow-hidden',
  next: 'flex items-center gap-1 overflow-hidden border-t-2 border-bg-canvas',
} as const;

/** 줄 높이: 첫 줄 = 한 줄 자리 − 칸 아래 선 2(칸의 border-b 가 채운다), 다음 줄 = 한 줄 자리(위 틈 2 포함) */
export const PORTFOLIO_ROW_STACK_LINE_HEIGHT = {
  first: `calc(${PORTFOLIO_ROW_LINE_HEIGHT} - 2px)`,
  next: PORTFOLIO_ROW_LINE_HEIGHT,
} as const;

/** 회사명 · 펀드명 누름 자리: 글자처럼 보이는 버튼 */
export const PORTFOLIO_ROW_LINK_CLASS =
  'min-w-0 cursor-pointer truncate rounded-sm text-left hover:underline';
